<template>
	<div class="content">
		<div class="section">
			<div class="section-tit">
				{{ isEdit ? '게시글 수정' : '게시글 작성' }}
				<span class="sub">{{ cateName }}</span>
			</div>

			<table class="table type-input">
				<colgroup>
					<col width="15%" />
					<col width="85%" />
				</colgroup>
				<!-- 게시판(카테고리)은 등록 시에만 정할 수 있다. 첨부 실패 후 재시도는 /board/update 로
				     나가고 update 는 카테고리·상단고정을 바꾸지 않으므로, 글이 만들어진 뒤에는
				     (postUid 존재) 이 컨트롤을 숨겨 "바꿨는데 반영 안 되는" 오해를 막는다. -->
				<tr v-if="!isEdit && !postUid">
					<th>게시판</th>
					<td>
						<select v-model="form.cateUid" class="form-control select sm" @change="onCateChange">
							<option v-for="c in writableCates" :key="'wc_' + c.board_category_uid" :value="c.board_category_uid">
								{{ c.name }}
							</option>
						</select>
						<span v-if="!writableCates.length" class="txt-c--red">글을 쓸 수 있는 게시판이 없습니다.</span>
					</td>
				</tr>
				<tr>
					<th>제목</th>
					<td><input type="text" class="form-control" v-model="form.title" maxlength="200" /></td>
				</tr>
				<!-- 옵션 행: 안에 실제로 보일 컨트롤이 하나라도 있을 때만 그린다.
				     (등록 이후 상단고정이 숨으면서 관리자에게 빈 행이 남던 문제)
				     신규 작성이면 '전체 알람' 이 항상 있으므로 행도 항상 보인다. -->
				<tr v-if="currentCate && (currentCate.confirm_flag === 'Y' || (!isEdit && !postUid))">
					<th>옵션</th>
					<td>
						<!-- B-4 전체공지: 확인추적 카테고리에서만 의미가 있다 -->
						<label v-if="currentCate.confirm_flag === 'Y'" class="input-checkbox">
							<input type="checkbox" v-model="form.notice_all_flag" true-value="Y" false-value="N" />
							<span class="checkbox"></span>
							<span class="text">전체공지 (상세 하단에 "확인" 버튼 노출 + 직원별 확인 여부 집계)</span>
						</label>
						<!-- B-3 상단 고정: 관리자만. 게시판 선택과 같은 이유로 등록 이후에는 숨긴다. -->
						<label v-if="canPin && !isEdit && !postUid" class="input-checkbox">
							<input type="checkbox" v-model="form.pin_flag" true-value="Y" false-value="N" />
							<span class="checkbox"></span>
							<span class="text">상단 고정</span>
						</label>
						<!-- CE 전체 알람: 등록 즉시 재직 직원 전원에게 쪽지. 모든 카테고리에서 쓸 수 있고,
						     수정 화면에는 없다(수정 때마다 재발송되는 스팸 방지 — 서버도 add 에서만 발송). -->
						<label v-if="!isEdit && !postUid" class="input-checkbox">
							<input type="checkbox" v-model="form.alarm_all_flag" true-value="Y" false-value="N" />
							<span class="checkbox"></span>
							<span class="text">전체 알람 (등록 즉시 전 직원에게 쪽지 알림)</span>
						</label>
					</td>
				</tr>
			</table>
		</div>

		<!-- ── B-10 추천매물: 기존 매물 DB 에서 불러오기 ───────────────────────── -->
		<div class="section" v-if="isItemCate">
			<div class="section-tit">
				추천 매물
				<span class="sub">물건번호 · 물건명 · 주소로 기존 매물을 검색해 선택하세요 (정보를 다시 입력하지 않습니다)</span>
			</div>

			<div class="search-top" v-if="!selectedItem || searchOpen">
				<div class="left">
					<label class="input-label">물건번호</label>
					<input class="form-control xsm" type="text" v-model="itemSearch.bid" @keyup.enter="searchItem" />
					<label class="input-label">물건명</label>
					<input class="form-control sm" type="text" v-model="itemSearch.name" @keyup.enter="searchItem" />
					<label class="input-label">주소</label>
					<input class="form-control sm" type="text" v-model="itemSearch.addr" @keyup.enter="searchItem" />
				</div>
				<div class="right">
					<button type="button" class="btn btn-primary btn-sm" @click="searchItem">검색</button>
				</div>
			</div>

			<div class="table-wrap" v-if="(!selectedItem || searchOpen) && searchList.length">
				<table class="table sm click">
					<tr>
						<th>물건번호</th>
						<th>상태</th>
						<th>물건명</th>
						<th>주소</th>
						<th>매매가</th>
						<th>대지(평)</th>
						<th>연면적(평)</th>
						<th>담당자</th>
						<th>선택</th>
					</tr>
					<tr v-for="b in searchList" :key="'sb_' + b.building_uid">
						<td>{{ b.building_uid }}</td>
						<td>{{ $ITEM_STATE[b.sell_status] }}</td>
						<td>{{ b.building_name }}</td>
						<td>{{ $normalizeAddr(b.jibun_addr) }}</td>
						<td>{{ $formatMoney(b.sell_price, $MONEY_FORMAT_TYPE.MUCH) }}</td>
						<td>{{ $formatArea(b.land_size_p) }}</td>
						<td>{{ $formatArea(b.total_size_p) }}</td>
						<td>{{ b.building_mem_name }}</td>
						<td><button type="button" class="btn btn-xsm btn-primary" @click="selectItem(b)">선택</button></td>
					</tr>
				</table>
			</div>
			<div v-if="(!selectedItem || searchOpen) && searched && !searchList.length" class="txt-c--grey">
				검색 결과가 없습니다.
			</div>

			<!-- 선택 매물의 자동 노출 정보 (B-10) -->
			<table class="table type-input mt-2" v-if="selectedItem">
				<colgroup>
					<col width="15%" /><col width="35%" /><col width="15%" /><col width="35%" />
				</colgroup>
				<tr>
					<th>물건번호</th>
					<td>
						{{ selectedItem.building_uid }}
						<button type="button" class="btn btn-xsm btn-secondary" @click="openOrigin">원본 상세보기</button>
						<button type="button" class="btn btn-xsm btn-light" @click="searchOpen = true">매물 변경</button>
					</td>
					<th>물건명</th>
					<td>{{ selectedItem.building_name }}</td>
				</tr>
				<tr>
					<th>주소</th>
					<td colspan="3">{{ $normalizeAddr(selectedItem.jibun_addr) }}</td>
				</tr>
				<tr>
					<th>매매가</th>
					<td>{{ $formatMoney(selectedItem.sell_price, $MONEY_FORMAT_TYPE.MUCH) }}</td>
					<th>평당가</th>
					<td>{{ $formatNumber(selectedItem.total_size_py_price) }}</td>
				</tr>
				<tr>
					<th>대지면적(평)</th>
					<td>{{ $formatArea(selectedItem.land_size_p) }}</td>
					<th>연면적(평)</th>
					<td>{{ $formatArea(selectedItem.total_size_p) }}</td>
				</tr>
				<tr>
					<th>임대수익률</th>
					<td>{{ selectedItem.total_income_rate != null ? selectedItem.total_income_rate + '%' : '' }}</td>
					<th>담당자</th>
					<td>{{ selectedItem.building_mem_name }}</td>
				</tr>
			</table>

			<div class="txt-c--grey mt-1" v-if="selectedItem">
				※ 위 매물 정보는 게시글에 복사 저장하지 않습니다. 원본 매물의 가격·상태가 바뀌면 게시글에도 자동 반영됩니다.
			</div>

			<table class="table type-input mt-2">
				<colgroup>
					<col width="15%" />
					<col width="85%" />
				</colgroup>
				<tr>
					<th>추천 사유</th>
					<td><textarea class="form-control textarea" rows="3" v-model="form.reco_reason"></textarea></td>
				</tr>
				<tr>
					<th>투자 포인트</th>
					<td><textarea class="form-control textarea" rows="3" v-model="form.reco_point"></textarea></td>
				</tr>
				<tr>
					<th>참고사항</th>
					<td><textarea class="form-control textarea" rows="3" v-model="form.reco_etc"></textarea></td>
				</tr>
			</table>
		</div>

		<!-- ── 매수요청: 원하는 조건만 등록 — 매물 연결은 상세 화면에서 담당자들이 추가한다 ── -->
		<div class="section" v-if="isRequestCate">
			<div class="section-tit">
				요청 조건
				<span class="sub">원하는 조건만 적으면 됩니다 — 조건에 맞는 매물은 담당자들이 상세 화면에서 '추천매물 추가' 로 연결합니다</span>
			</div>
			<table class="table type-input">
				<colgroup>
					<col width="15%" /><col width="35%" /><col width="15%" /><col width="35%" />
				</colgroup>
				<tr>
					<th>예산</th>
					<td><input type="text" class="form-control" v-model="form.req_budget" maxlength="200" placeholder="예: 50억 내외 (대출 포함 70억까지)" /></td>
					<th>희망 지역</th>
					<td><input type="text" class="form-control" v-model="form.req_region" maxlength="200" placeholder="예: 강남구 · 서초구 역세권" /></td>
				</tr>
				<tr>
					<th>용도</th>
					<td><input type="text" class="form-control" v-model="form.req_use" maxlength="200" placeholder="예: 사옥용 통건물, 수익형 상가" /></td>
					<th>희망 면적</th>
					<td><input type="text" class="form-control" v-model="form.req_area" maxlength="200" placeholder="예: 대지 100평 이상" /></td>
				</tr>
			</table>
			<div class="txt-c--grey mt-1">※ 상세한 배경 · 추가 조건은 아래 내용에 작성하세요.</div>
		</div>

		<div class="section">
			<div class="section-tit">내용</div>
			<textarea class="form-control textarea" rows="14" v-model="form.memo"></textarea>

			<!-- B-6 첨부: 이미지 / 동영상 / 파일.
			     예전에는 "글을 먼저 저장 → 첨부를 따로 업로드" 2단계였는데, 저장 후에도 화면이 그대로
			     남아 사용자가 완료를 인지하지 못하고 저장을 다시 눌러 같은 글이 여러 건 등록됐다.
			     지금은 파일을 미리 담아두고 저장 버튼 한 번으로 글 + 첨부가 함께 등록된다. -->
			<div class="section-tit sub mt-3">
				첨부파일
				<span class="sub">이미지 · 동영상 · 문서 (파일당 최대 {{ maxMb }}MB) — 저장을 누르면 글과 함께 등록됩니다</span>
			</div>
			<!-- 브라우저 기본 파일 컨트롤은 이 화면에서만 튀어 보인다(계약 상세는 이미 버튼 방식).
			     input 은 숨기고 앱의 버튼 스타일로 통일한다. -->
			<div class="d-flex align-items-center">
				<input type="file" ref="fileInput" class="d-none" multiple @change="onFilePick" :disabled="saving" />
				<button
					type="button"
					class="btn btn-sm btn-secondary"
					@click="$refs.fileInput.click()"
					:disabled="saving"
					aria-describedby="boardFilePicked"
				>
					파일 선택
				</button>
				<!-- input 을 숨기면 개수 변화가 화면 낭독기에 전달되지 않는다 → status/aria-live 로 알린다. -->
				<span id="boardFilePicked" class="txt-c--grey m-l--1" role="status" aria-live="polite">
					{{ pickedFiles.length ? pickedFiles.length + '개 선택됨' : '선택된 파일 없음' }}
				</span>
			</div>
			<div class="table-wrap mt-2" v-if="pickedFiles.length">
				<table class="table sm">
					<colgroup>
						<col width="12%" /><col /><col width="14%" /><col width="10%" />
					</colgroup>
					<tr>
						<th>구분</th>
						<th>등록할 파일</th>
						<th>크기</th>
						<th>제거</th>
					</tr>
					<tr v-for="(f, i) in pickedFiles" :key="'pf_' + i + '_' + f.name">
						<td>{{ pickedKind(f) }}</td>
						<td class="txt-left">{{ f.name }}</td>
						<td>{{ sizeText(f.size) }}</td>
						<td>
							<button type="button" class="btn btn-xsm btn-light" @click="removePicked(i)" :disabled="saving">
								제거
							</button>
						</td>
					</tr>
				</table>
			</div>

			<!-- 이미 등록돼 있는 첨부 (수정 화면) -->
			<div class="table-wrap mt-2" v-if="fileList.length">
				<table class="table sm">
					<colgroup>
						<col width="12%" /><col /><col width="14%" /><col width="10%" />
					</colgroup>
					<tr>
						<th>구분</th>
						<th>등록된 파일</th>
						<th>크기</th>
						<th>삭제</th>
					</tr>
					<tr v-for="f in fileList" :key="'bf_' + f.id">
						<td>{{ FILE_KIND[f.file_kind] || '파일' }}</td>
						<td class="txt-left">{{ f.org_name }}</td>
						<td>{{ sizeText(f.size) }}</td>
						<td>
							<button type="button" class="btn btn-xsm btn-danger" @click="deleteFile(f)" :disabled="saving">
								삭제
							</button>
						</td>
					</tr>
				</table>
			</div>
		</div>

		<div class="btn-wrap">
			<div class="txt-c--grey mb-1" v-if="progressText">{{ progressText }}</div>
			<!-- 업로드가 길어질 수 있으므로 첨부 업로드 구간에서는 취소 대신 "중단" 을 제공한다(무한 대기 방지).
			     글 저장 요청만 도는 구간에는 끊을 대상이 없어 취소를 비활성으로 둔다. -->
			<button v-if="!saving" type="button" class="btn btn-secondary" @click="btnCancel">취소</button>
			<button v-else-if="uploadPhase" type="button" class="btn btn-secondary" @click="btnStop" :disabled="cancelRequested">
				{{ cancelRequested ? '중단 중…' : '중단' }}
			</button>
			<button v-else type="button" class="btn btn-secondary" disabled>취소</button>
			<button type="button" class="btn btn-primary" @click="btnSave" :disabled="saving">
				{{ saving ? '등록 중…' : isEdit ? '수정' : '저장' }}
			</button>
		</div>
	</div>
</template>

<script>
const FILE_KIND = { image: '이미지', video: '동영상', file: '파일' };

// CI 멱등키. crypto.randomUUID 는 보안 컨텍스트(https/localhost)에서만 있고 운영은
// 평문 http 라 없을 수 있다 → getRandomValues, 그것도 없으면 Math.random 으로 내려간다.
// 서버는 이 값을 신뢰하지 않고 중복 판정에만 쓰므로 예측 가능성은 문제가 되지 않는다.
function newClientKey() {
	try {
		if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
		if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
			const a = new Uint8Array(16);
			crypto.getRandomValues(a);
			return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
		}
	} catch (e) { /* 아래 폴백 */ }
	return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
}

export default {
	name: 'BoardWrite',
	data() {
		return {
			FILE_KIND,
			cateList: [],
			isMaster: false,
			// 저장 후 첨부를 붙일 수 있도록 신규 작성에서도 postUid 를 보관한다.
			postUid: null,
			// CI 멱등키: 이 작성 화면 한 번에 하나. 응답이 유실돼 사용자가 저장을 다시
			// 눌러도 서버가 같은 키를 보고 기존 글을 돌려주므로 글·전체알람이 중복되지
			// 않는다. 화면을 새로 열면 컴포넌트가 다시 만들어져 새 키가 생긴다.
			clientKey: newClientKey(),
			form: {
				cateUid: null,
				title: '',
				memo: '',
				notice_all_flag: 'N',
				pin_flag: 'N',
				alarm_all_flag: 'N',
				item_kind: 'building',
				building_uid: null,
				reco_reason: '',
				reco_point: '',
				reco_etc: '',
				req_budget: '',
				req_region: '',
				req_use: '',
				req_area: '',
			},
			itemSearch: { bid: '', name: '', addr: '' },
			searchList: [],
			searched: false,
			searchOpen: false,
			selectedItem: null,

			fileList: [],
			// 저장 버튼을 누를 때 함께 업로드할 대기 목록. 업로드에 성공한 파일은 여기서 빠지고
			// 실패한 파일만 남아 재시도 대상이 된다.
			pickedFiles: [],
			maxMb: 200,
			saving: false,
			cancelRequested: false,
			// 첨부를 실제로 올리는 구간에서만 "중단" 을 노출한다(글 저장 요청 중에는 끊을 게 없다).
			uploadPhase: false,
			// 저장 중 사용자가 확인을 누르고 화면을 떠났다 → 이후 알림·이동을 모두 생략한다.
			leftWhileSaving: false,
			progressText: '',
		};
	},
	computed: {
		isEdit() {
			return !!this.$route.params.id;
		},
		currentCate() {
			return this.cateList.find(c => c.board_category_uid == this.form.cateUid) || null;
		},
		writableCates() {
			return this.cateList.filter(c => c.can_write);
		},
		isItemCate() {
			return !!(this.currentCate && this.currentCate.item_flag === 'Y');
		},
		isRequestCate() {
			return !!(this.currentCate && this.currentCate.request_flag === 'Y');
		},
		canPin() {
			return this.isMaster;
		},
		cateName() {
			return this.currentCate ? this.currentCate.name : '';
		},
	},
	created() {
		this.init();
	},
	// 저장/업로드가 도는 중에 화면을 떠나면(헤더 메뉴·뒤로가기) 진행 중인 등록 결과를 확인할 수
	// 없고, 돌아와서 다시 저장하면 같은 글이 또 만들어질 수 있다 → 명시적으로 확인을 받는다.
	// 단, 토큰 만료로 $apiFILE/$apiPOST 가 로그인 화면으로 강제 이동시키는 경우는 막지 않는다
	// (실측: 업로드 중 401 이 나면 이 확인창이 로그인 리다이렉트를 가로막았다).
	// 라우트 이름만으로는 자발적 로그아웃과 구분되지 않아 seon 의 인증 리다이렉트 표시를 함께 본다.
	beforeRouteLeave(to, from, next) {
		if (!this.saving) return next();
		// 인증 만료(401 강제 이동)나 이미 로그아웃 확인을 받은 이동은 다시 묻지 않는다.
		const asked = to.name === 'LoginView' && (this.$isAuthRedirect() || this.$isLeaveConfirmed());
		if (!asked && !window.confirm('등록이 진행 중입니다. 지금 나가면 결과를 확인할 수 없습니다. 나가시겠습니까?')) {
			return next(false);
		}
		// 어떤 경로로 떠나든(401 포함) 남은 업로드를 끊고, 화면을 떠난 뒤 알림·이동이 튀지 않게 표시한다.
		// 공용 헬퍼라 401 이 이 화면의 요청이 아닐 수도 있어 인증 이동에서도 반드시 끊어야 한다.
		this.leftWhileSaving = true;
		this.cancelRequested = true;
		next();
	},
	methods: {
		async init() {
			const re = await this.$apiGET('/admin/api/board/category');
			if (!re || !re.list) return;
			this.cateList = re.list;
			this.isMaster = !!(re.scope && re.scope.isMaster);

			if (this.isEdit) {
				this.postUid = Number(this.$route.params.id);
				await this.loadPost();
				await this.loadFiles();
				return;
			}
			const q = this.$route.query.cate;
			const pick = this.writableCates.find(c => String(c.board_category_uid) === String(q)) || this.writableCates[0];
			this.form.cateUid = pick ? pick.board_category_uid : null;
		},
		async loadPost() {
			const re = await this.$apiGET('/admin/api/board/item?id=' + this.postUid);
			if (!re || !re.item) return;
			const it = re.item;
			if (!it.can_edit) {
				alert('수정 권한이 없습니다.');
				this.$router.replace({ name: 'BoardDetail', params: { id: this.postUid } });
				return;
			}
			this.form.cateUid = it.board_category_uid;
			this.form.title = it.title;
			this.form.memo = it.memo;
			this.form.notice_all_flag = it.notice_all_flag;
			this.form.item_kind = it.item_kind || 'building';
			this.form.building_uid = it.building_uid;
			this.form.reco_reason = it.reco_reason || '';
			this.form.reco_point = it.reco_point || '';
			this.form.reco_etc = it.reco_etc || '';
			this.form.req_budget = it.req_budget || '';
			this.form.req_region = it.req_region || '';
			this.form.req_use = it.req_use || '';
			this.form.req_area = it.req_area || '';
			if (it.building_uid) {
				// B-12: 원본이 삭제되면 조인 결과가 비어 매물 카드가 빈 값이 된다 → 명시적으로 알린다.
				if (it.item_deleted) {
					this.selectedItem = null;
					this.searchOpen = true;
					alert('연결된 원본 매물이 삭제되었습니다. 다른 매물로 변경하거나 게시글을 삭제하세요.');
				} else {
					this.selectedItem = {
						building_uid: it.building_uid,
						building_name: it.item_name,
						jibun_addr: it.item_jibun_addr,
						sell_price: it.item_sell_price,
						total_size_py_price: it.item_py_price,
						land_size_p: it.item_land_size_p,
						total_size_p: it.item_total_size_p,
						total_income_rate: it.item_income_rate,
						building_mem_name: it.item_mgr_name,
					};
				}
			}
		},
		async loadFiles() {
			if (!this.postUid) return;
			const re = await this.$apiGET('/admin/api/board/file?id=' + this.postUid);
			if (Array.isArray(re)) this.fileList = re;
		},
		onCateChange() {
			this.selectedItem = null;
			this.searchList = [];
			this.searched = false;
			this.form.building_uid = null;
		},
		searchItem() {
			// 빈 검색어는 쿼리에서 아예 뺀다 — 공통 bid-guard(J-2, backend/routes/index.js)가
			// bid='' 를 400 으로 차단해서, 빈 bid 를 함께 보내면 물건명·주소 단독 검색이 통째로 막힌다.
			const q = new URLSearchParams({ kind: this.form.item_kind });
			for (const [k, v] of Object.entries(this.itemSearch)) {
				const t = String(v || '').trim();
				if (t) q.set(k, t);
			}
			if (!q.has('bid') && !q.has('name') && !q.has('addr')) {
				alert('물건번호 · 물건명 · 주소 중 하나를 입력하세요.');
				return;
			}
			this.$apiGET('/admin/api/board/item/search?' + q.toString()).then(re => {
				this.searched = true;
				this.searchList = Array.isArray(re) ? re : [];
			});
		},
		selectItem(b) {
			this.selectedItem = b;
			this.form.building_uid = b.building_uid;
			this.searchOpen = false;
			this.searchList = [];
		},
		openOrigin() {
			if (!this.selectedItem) return;
			const target = this.form.item_kind === 'rent_building' ? '/LeaseMapDetail/' : '/MapDetail/';
			const w = window.screen.availWidth;
			const h = window.screen.availHeight;
			window.open(target + this.selectedItem.building_uid, '', `width=${w}, height=${h}, resizable=no, status=no`);
		},
		validate() {
			if (!this.form.cateUid) {
				alert('게시판을 선택하세요.');
				return false;
			}
			if (!this.form.title || !this.form.title.trim()) {
				alert('제목을 입력하세요.');
				return false;
			}
			if (this.isItemCate && !this.form.building_uid) {
				alert('추천할 매물을 검색해서 선택하세요.');
				return false;
			}
			return true;
		},
		async btnSave() {
			// 중복 등록 방지 1: 진행 중 재클릭 차단(버튼 disabled 와 이중 안전망).
			if (this.saving) return;
			if (!this.validate()) return;

			// 중복 등록 방지 2: 첨부 업로드 단계에서 실패해 사용자가 저장을 다시 누르는 경우.
			// postUid 가 이미 있으면(=글은 등록됨) add 가 아니라 update 로 보낸다.
			const isUpdate = this.isEdit || !!this.postUid;
			const body = {
				cateUid: this.form.cateUid,
				postUid: this.postUid,
				title: this.form.title,
				memo: this.form.memo,
				notice_all_flag: this.form.notice_all_flag,
				pin_flag: this.form.pin_flag,
				// CE 전체 알람: 신규 등록에서만 의미가 있다 (서버도 add 에서만 발송).
				alarm_all_flag: isUpdate ? 'N' : this.form.alarm_all_flag,
				// CI 멱등키 — add 경로에서만 의미가 있다.
				client_key: isUpdate ? null : this.clientKey,
				item_kind: this.isItemCate ? this.form.item_kind : null,
				building_uid: this.isItemCate ? this.form.building_uid : null,
				reco_reason: this.form.reco_reason,
				reco_point: this.form.reco_point,
				reco_etc: this.form.reco_etc,
				// isRequestCate 로 거르지 않고 항상 보낸다 — 카테고리가 비활성(use_flag='N')이면
				// 탭 목록에 없어 currentCate 가 null 이 되는데, 그때 null 을 보내면 제목만 고쳐도
				// 조건 4필드가 통째로 유실된다(Codex P1). 서버가 카테고리 형식으로 최종 판정한다.
				req_budget: this.form.req_budget,
				req_region: this.form.req_region,
				req_use: this.form.req_use,
				req_area: this.form.req_area,
			};
			const url = isUpdate ? '/admin/api/board/update' : '/admin/api/board/add';

			this.saving = true;
			this.cancelRequested = false;
			this.leftWhileSaving = false;
			this.progressText = '게시글 저장 중…';
			this.$setBusy(true); // 헤더 로그아웃이 확인 없이 토큰을 지우지 않도록
			try {
				const re = await this.$apiPOST(url, body);
				// 실패는 $apiPOST 가 이미 alert 했다. false/undefined 면 화면을 옮기지 않는다
				// (저장 안 됐는데 목록으로 튕기면 사용자가 성공으로 오해한다).
				if (!re || (re !== true && !re.ok)) return;
				if (!isUpdate) this.postUid = re.id;

				const { failed, aborted, done } = await this.uploadPicked();
				// 401 로 로그인 화면으로 밀려난 상태다 — 여기서 알림·이동을 더 하면 로그인 화면 위에서 튄다.
				if (aborted === 'auth') return;
				// 사용자가 확인을 누르고 이미 다른 화면으로 갔다 → 뒤늦은 알림·되돌아가기를 하지 않는다.
				if (this.leftWhileSaving) return;
				// 문구는 사용자가 시작한 작업 기준(신규 작성 중 첨부 실패 후 재시도해도 "등록").
				const word = this.isEdit ? '수정' : '등록';
				const uploadedText = done > 0 ? `\n첨부파일 ${done}개는 등록되었습니다.` : '';
				if (aborted === 'user') {
					alert(
						`게시글은 ${word}되었습니다.${uploadedText}\n중단하여 남은 ${failed.length}개는 올리지 않았습니다.\n` +
							`남은 파일은 "등록할 파일" 목록에 그대로 있으니 다시 저장하거나 제거하세요.`
					);
					await this.loadFiles();
					return;
				}
				if (failed.length) {
					// 글은 저장됐으므로 화면을 유지하고 실패한 파일만 남긴다 → 저장을 다시 눌러도
					// update 로 나가기 때문에 글이 중복 등록되지 않는다.
					alert(
						`게시글은 ${word}되었습니다.${uploadedText}\n다만 첨부파일 ${failed.length}개를 올리지 못했습니다.\n` +
							`(${failed.join(', ')})\n"등록할 파일" 목록에 남아 있으니 다시 시도하거나 제거 후 저장하세요.`
					);
					await this.loadFiles();
					return;
				}
				alert(`${word}되었습니다.`);
				this.$router.push({ name: 'BoardDetail', params: { id: this.postUid }, query: { cate: this.form.cateUid } });
			} finally {
				this.saving = false;
				this.cancelRequested = false;
				this.uploadPhase = false;
				this.progressText = '';
				this.$setBusy(false);
			}
		},
		// 업로드 중에는 취소 버튼이 "중단" 으로 바뀐다. 현재 전송 중인 파일은 끝까지 가고
		// 남은 대기 파일은 보내지 않는다($apiFILE 이 AbortController 를 노출하지 않으므로 파일 경계에서 멈춘다).
		btnStop() {
			this.cancelRequested = true;
			this.progressText = '중단 요청됨 — 현재 파일까지만 올립니다…';
		},
		// 대기 목록을 순차 업로드한다.
		// 남길 목록은 파일명이 아니라 File 객체 기준으로 고른다(같은 이름의 다른 파일이 섞여도 정확).
		// 반환값: { failed:[파일명], aborted:'auth'|'user'|null, done:업로드성공수 }
		async uploadPicked() {
			const queue = this.pickedFiles.slice();
			const stillFailed = [];
			let aborted = null;
			let done = 0;
			this.uploadPhase = queue.length > 0;
			for (let i = 0; i < queue.length; i++) {
				if (this.cancelRequested) {
					// 사용자가 "중단" 을 눌렀다 — 남은 파일은 대기 목록에 그대로 둔다.
					aborted = 'user';
					stillFailed.push(...queue.slice(i));
					break;
				}
				const f = queue[i];
				this.progressText = `첨부파일 업로드 중… (${i + 1}/${queue.length}) ${f.name}`;
				// $apiFILE 은 (path, bid, fileObj, fileName) 시그니처로 form 에 bid/fileName/file 을 담는다.
				// 백엔드가 req.body.postUid || req.body.bid 를 모두 받으므로 그대로 재사용한다.
				const re = await this.$apiFILE('/admin/api/board/file/add', this.postUid, f, f.name);
				// $apiFILE 은 401 에서 undefined 를 돌려주고 이미 로그인 화면으로 이동시킨다.
				// 실패로 취급해 계속 돌리면 로그인 화면에서 남은 업로드가 이어지고 알림까지 뜬다 → 즉시 중단.
				if (re === undefined) {
					aborted = 'auth';
					stillFailed.push(...queue.slice(i));
					break;
				}
				if (re !== true) stillFailed.push(f);
				else done++;
			}
			this.pickedFiles = stillFailed;
			this.uploadPhase = false;
			this.progressText = '';
			return { failed: stillFailed.map(f => f.name), aborted, done };
		},
		btnCancel() {
			if (this.isEdit) {
				this.$router.push({ name: 'BoardDetail', params: { id: this.postUid }, query: { cate: this.form.cateUid } });
			} else {
				this.$router.push({ name: 'Board', query: { cate: this.form.cateUid } });
			}
		},
		onFilePick(e) {
			const files = e.target.files ? Array.from(e.target.files) : [];
			const tooBig = [];
			for (const f of files) {
				// 서버(multer) 상한과 같은 값을 미리 걸러 "글은 저장됐는데 파일만 실패" 를 줄인다.
				if (f.size > this.maxMb * 1024 * 1024) {
					tooBig.push(f.name);
					continue;
				}
				// 이름+크기가 같아도 서로 다른 파일일 수 있어(다른 폴더의 동명 계약서 등) 조용히 버리지 않는다.
				// 실수로 같은 파일을 두 번 고른 경우는 목록에 두 줄로 보이므로 "제거" 로 지울 수 있다.
				this.pickedFiles.push(f);
			}
			if (tooBig.length) alert(`다음 파일은 ${this.maxMb}MB 를 넘어 제외했습니다.\n${tooBig.join(', ')}`);
			// 같은 파일을 다시 고를 수 있도록 input 을 비운다.
			if (this.$refs.fileInput) this.$refs.fileInput.value = '';
		},
		removePicked(i) {
			this.pickedFiles.splice(i, 1);
		},
		pickedKind(f) {
			const mt = String(f.type || '').toLowerCase();
			if (mt.startsWith('image/')) return FILE_KIND.image;
			if (mt.startsWith('video/')) return FILE_KIND.video;
			return FILE_KIND.file;
		},
		deleteFile(f) {
			if (!confirm('첨부파일을 삭제하시겠습니까?')) return;
			this.$apiPOST('/admin/api/board/file/delete', { id: f.id }).then(re => {
				if (re !== true) return;
				this.loadFiles();
			});
		},
		sizeText(size) {
			const n = Number(size) || 0;
			if (n >= 1024 * 1024) return (n / 1024 / 1024).toFixed(1) + 'MB';
			if (n >= 1024) return Math.round(n / 1024) + 'KB';
			return n + 'B';
		},
	},
};
</script>

<style></style>
